import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectConversations,
  selectConversationViews,
  selectNeedsAttentionCount,
} from '@/features/adviser-chat/adviser-chat.selectors'
import {
  CONVERSATION_VIEWS,
  RAIL_FILTERS,
  type ConversationView,
  type RailFilter,
} from '@/features/adviser-chat/lib/constants'
import type { ConversationSummary } from '@/features/adviser-chat/lib/types'
import type { ConversationViewStatus } from '../adviser-chat.slice'
import { fetchConversationView } from '../adviser-chat.actions'

export interface RailSection {
  view: ConversationView
  label: string
  conversations: ConversationSummary[]
  hasMore: boolean
  status: ConversationViewStatus
}

const SECTION_LABELS: Record<ConversationView, string> = {
  [CONVERSATION_VIEWS.open]: 'Open conversations',
  [CONVERSATION_VIEWS.history]: 'History',
  [CONVERSATION_VIEWS.needsAttention]: 'Needs attention',
}

const VIEWS_BY_FILTER: Record<RailFilter, ConversationView[]> = {
  [RAIL_FILTERS.all]: [CONVERSATION_VIEWS.open, CONVERSATION_VIEWS.history],
  [RAIL_FILTERS.needsAttention]: [CONVERSATION_VIEWS.needsAttention],
}

export const useConversationRail = () => {
  const dispatch = useAppDispatch()
  const conversations = useReselector(selectConversations)
  const views = useReselector(selectConversationViews)
  const needsAttentionCount = useReselector(selectNeedsAttentionCount)
  const [filter, setFilter] = useState<RailFilter>(RAIL_FILTERS.all)

  const visibleViews = VIEWS_BY_FILTER[filter]

  useEffect(() => {
    for (const view of visibleViews) {
      if (views[view].status === 'idle') dispatch(fetchConversationView(view))
    }
  }, [dispatch, views, visibleViews])

  const byId = useMemo(
    () =>
      new Map(
        conversations.map((conversation) => [conversation.id, conversation])
      ),
    [conversations]
  )

  const sections = useMemo(
    (): RailSection[] =>
      visibleViews.map((view) => ({
        view,
        label: SECTION_LABELS[view],
        conversations: views[view].ids
          .map((id) => byId.get(id))
          .filter((conversation): conversation is ConversationSummary =>
            Boolean(conversation)
          ),
        hasMore: views[view].hasMore,
        status: views[view].status,
      })),
    [byId, views, visibleViews]
  )

  const loadMore = useCallback(
    (view: ConversationView) => {
      dispatch(fetchConversationView(view, views[view].ids.length))
    },
    [dispatch, views]
  )

  return { filter, setFilter, needsAttentionCount, sections, loadMore }
}
